import { Storage, getLocalStorage } from '@utils/storage';
import { Subject, Observable } from 'rxjs';

import { executeWithAuth0Retry } from './auth0.retry';
import globalWindow from '@utils/globals';

interface AccessToken
  extends Readonly<{
    id_token: string;
    bearer_type: string;
    expires_in: number;
  }> {}

interface Secrets {
  accessToken?: AccessToken;
  accessTokenClientId?: string;
  offset?: number;
  /** Required when requesting a new Access Token w/o re-authenticating the user. */
  refreshToken?: string;
  refreshTokenClientId?: string;
}

interface PersistedSecrets extends Pick<Secrets, 'accessTokenClientId' | 'offset' | 'refreshToken' | 'refreshTokenClientId'> {
  accessToken?: string;
}

interface Options
  extends Readonly<{
    secondsOkToUseTokenBeforeExpiry: number;
  }> {}

const defaultOptions: Options = {
  secondsOkToUseTokenBeforeExpiry: 10,
};

export interface DecodedToken
  extends Readonly<{
    aud: string;
    azp: string;
    exp: number;
    iat: number;
    iss: string;
    sub: string;
  }> {}

export class TokenService {
  private readonly accessTokenSubject: Subject<AccessToken | null>;
  private generateAccessTokenPromise: Promise<AccessToken | null> | null = null;
  private readonly options: Options = defaultOptions;
  private refreshTimeoutId: number | undefined;
  private secrets: Secrets = {};
  private readonly storage: Storage;

  constructor(options?: Partial<Options>) {
    this.options = { ...defaultOptions, ...(options || {}) };
    this.accessTokenSubject = new Subject();
    this.storage = getLocalStorage();
    this.authenticate();
  }

  public accessTokenObservable(): Observable<AccessToken | null> {
    return this.accessTokenSubject;
  }

  public authenticate(): void {
    this.checkTokenExpirationAndQueueRefresh();
  }

  public getToken(): string | null {
    return this.getAccessToken();
  }

  public getTokenAsync = async (): Promise<string | null> => {
    await this.checkTokenExpirationAndQueueRefresh();
    return this.getAccessToken();
  };

  public setToken(token: AccessToken | null, clientId: string, refreshToken?: string, serverTime?: number) {
    this.writeToSecretsAndAlertListeners(token, clientId, refreshToken, serverTime);
    this.clearRefreshTimeoutId();
    if (token) {
      this.checkTokenExpirationAndQueueRefresh();
    }
  }

  public deleteSecrets() {
    this.storage.setItem('secrets', undefined);
    this.clearRefreshTimeoutId();
    this.broadcastTokenUpdate(null);
  }

  private attemptToRefreshTokenAndWrite = async (): Promise<AccessToken | null> => {
    // If there isn't a promise, create and set it
    if (!this.generateAccessTokenPromise) {
      this.clearRefreshTimeoutId();
      // eslint-disable-next-line no-async-promise-executor
      this.generateAccessTokenPromise = new Promise<AccessToken | null>(async resolve => {
        let newAccessToken: AccessToken | null = this.secrets.accessToken || null;
        // Abort if missing the required API arguments.
        if (this.secrets.accessTokenClientId && this.secrets.refreshToken) {
          try {
            const res = await executeWithAuth0Retry(() =>
              fetch('https://withjoy.auth0.com/delegation', {
                method: 'POST',
                mode: 'cors',
                headers: {
                  'Content-Type': 'application/json',
                  Accept: 'application/json',
                },
                body: JSON.stringify({
                  client_id: this.secrets.accessTokenClientId,
                  grant_type: 'urn:ietf:params:oauth:grant-type:jwt-bearer',
                  target: this.secrets.accessTokenClientId,
                  scope: 'openid',
                  api_type: 'app',
                  refresh_token: this.secrets.refreshToken,
                }),
              })
            );
            newAccessToken = await res.json();
          } catch (err) {
            // Unable to refresh - have them re auth
            newAccessToken = null;
            this.secrets.refreshToken = undefined;
          }
        }

        this.writeToSecretsAndAlertListeners(newAccessToken);
        resolve(newAccessToken);
      }).finally(() => {
        // Remove the promise and begin a new refresh cycle.
        this.generateAccessTokenPromise = null;
        if (this.secrets.accessTokenClientId && this.secrets.refreshToken) {
          this.checkTokenExpirationAndQueueRefresh();
        }
      });
    }

    // This problem will always resolve with either:
    // 1. the new access token
    // 2. the current token or null
    return this.generateAccessTokenPromise;
  };

  private checkTokenExpirationAndQueueRefresh = async (): Promise<AccessToken | null> => {
    this.collectSavedSecrets();

    const decodedToken = this.getDecodedAccessToken();
    if (decodedToken) {
      // A user is currently logged in.
      // Refresh or queue a refresh, if necessary.

      const timeLeft = this.getTimeLeftInSecondsBeforeExpiration(decodedToken, this.secrets.offset);
      const { secondsOkToUseTokenBeforeExpiry } = this.options;

      const hasTokenExpired = timeLeft <= 0;
      const hasTokenExceedExpiryThreshold = timeLeft < secondsOkToUseTokenBeforeExpiry;
      const canUseCurrentToken = !hasTokenExpired && !hasTokenExceedExpiryThreshold;
      const shouldQueueTokenRefresh = canUseCurrentToken && !this.refreshTimeoutId;

      let promiseResolve: Promise<AccessToken | null>;
      if (canUseCurrentToken) {
        // Sufficient time remaining before token expiry
        // Set time out to automaticaly refresh - factoring in threshold allottment
        if (shouldQueueTokenRefresh) {
          this.refreshTimeoutId = globalWindow.setTimeout?.(() => {
            this.attemptToRefreshTokenAndWrite();
          }, (timeLeft - secondsOkToUseTokenBeforeExpiry) * 1000);
        }

        promiseResolve = Promise.resolve(this.secrets.accessToken || null);
      } else {
        promiseResolve = this.attemptToRefreshTokenAndWrite();
      }

      return promiseResolve;
    } else {
      // Regardless if there isn't an active access token, attempt to refresh the token.
      return this.attemptToRefreshTokenAndWrite();
    }
  };

  private broadcastTokenUpdate(nextToken: AccessToken | null): void {
    this.accessTokenSubject.next(nextToken);
  }

  private clearRefreshTimeoutId(): boolean {
    const isInProgress = !!this.refreshTimeoutId;
    clearTimeout(this.refreshTimeoutId);
    this.refreshTimeoutId = undefined;
    return isInProgress;
  }

  private collectSavedSecrets(): void {
    const secrets = this.storage.getItem('secrets');
    this.secrets = secrets ? this.parseLocalStorage(secrets) : {};
  }

  private getDecodedAccessToken(): DecodedToken | null {
    if (this.secrets.accessToken) {
      try {
        const { id_token } = this.secrets.accessToken;
        if (!id_token) {
          console.warn('No token to decode.');
          return null;
        }
        const decodedToken: DecodedToken = JSON.parse(atob(id_token.split('.')[1]));
        return decodedToken;
      } catch (e) {
        console.error(`Unable to decode token.`);
        console.error(e);
        return null;
      }
    }
    return null;
  }

  private getAccessToken(): string | null {
    if (this.secrets.accessToken) {
      return this.isTokenExpired() ? null : this.secrets.accessToken.id_token;
    } else {
      return null;
    }
  }

  private getTimeLeftInSecondsBeforeExpiration(decodedToken: DecodedToken, offset: number = 0): number {
    // The following logic is extracted from our current JWT module
    return decodedToken.exp - Math.floor((Date.now() - offset) / 1000);
  }

  private isTokenExpired(): boolean {
    const decodedToken = this.getDecodedAccessToken();
    // Slightly innacurate to say it's expired when the token doesn't exist
    return decodedToken ? this.getTimeLeftInSecondsBeforeExpiration(decodedToken) <= 0 : true;
  }

  private parseLocalStorage(secretsString: string): Secrets {
    try {
      const secrets = JSON.parse(secretsString);

      // we used to stringify access token itself so need to handle that case for backward compat
      // probably can remove this code after July 2020 (3 months from now)
      let accessToken = secrets.accessToken;
      if (typeof secrets.accessToken === 'string') {
        accessToken = JSON.parse(accessToken);
      }

      accessToken = accessToken || null;

      return { ...secrets, accessToken };
    } catch (e) {
      return {};
    }
  }

  private writeToSecretsAndAlertListeners(
    newToken: AccessToken | null,
    clientId?: string | null,
    refreshToken?: string,
    serverTime?: number
  ): void {
    this.secrets.accessToken = newToken || undefined;

    if (refreshToken) {
      this.secrets.refreshToken = refreshToken;
    }

    if (clientId) {
      this.secrets.accessTokenClientId = clientId;
      this.secrets.refreshTokenClientId = clientId;
    }

    if (serverTime) {
      this.secrets.offset = Date.now() - serverTime;
    }

    this.storage.setItem('secrets', this.prepareSecretsForWrite());

    this.broadcastTokenUpdate(newToken);
  }

  private prepareSecretsForWrite = (): string => {
    const { accessToken, ...restSecrets } = this.secrets;

    const withStringifiedAccessToken: PersistedSecrets = {
      ...restSecrets,
    };

    try {
      // All apps expect `accessToken` to be stringified when reading from local storage
      withStringifiedAccessToken.accessToken = JSON.stringify(accessToken);
    } catch (e) {
      // this.logger.ensureSecretsFormatError(e);
    }
    return JSON.stringify(withStringifiedAccessToken);
  };
}
